// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useState } from 'react';
import {
  TextInput,
  TextArea,
  Checkbox,
  Select,
  SelectItem,
  Button,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from '@carbon/react';
import { Close } from '@carbon/icons-react';
import { validateProcedure } from '../../shared/procedures';
import type { ProcedureDetail } from '../../shared/types/procedures';
import styles from './ProcedureForm.module.css';

type Contingency = ProcedureDetail['contingencies'][number];
type Equipment = ProcedureDetail['affectedEquipment'][number];
type Step = ProcedureDetail['procedure'][number];

interface ProcedureFormProps {
  procedure: ProcedureDetail;
  original: ProcedureDetail;
  classificationOptions: string[];
  equipmentTypeOptions: string[];
  onChange: (next: ProcedureDetail) => void;
  /** Create mode has no prior version to diff against — hides the
   *  "Changes since" summary rather than showing a meaningless diff of a
   *  blank draft against itself. */
  hideChanges?: boolean;
}

type RequiredFieldName =
  | 'title'
  | 'procedureNumber'
  | 'classification'
  | 'equipmentType'
  | 'description';

function RequiredLabel({ children }: { children: React.ReactNode }) {
  return (
    <>
      {children} <span className={styles.required}>*</span>
    </>
  );
}

const CHANGE_LABELS: [keyof ProcedureDetail, string][] = [
  ['title', 'Title'],
  ['procedureNumber', 'Procedure #'],
  ['version', 'Version'],
  ['classification', 'Classification'],
  ['equipmentType', 'Equipment type'],
  ['description', 'Description of work'],
  ['affectedEquipment', 'Affected equipment'],
  ['prerequisites', 'Prerequisites'],
  ['procedure', 'Procedure steps'],
  ['contingencies', 'Contingencies'],
  ['closeout', 'Closeout'],
  ['governance', 'Governance'],
];

function diffLabels(original: ProcedureDetail, current: ProcedureDetail): string[] {
  return CHANGE_LABELS.filter(
    ([field]) => JSON.stringify(original[field]) !== JSON.stringify(current[field])
  ).map(([, label]) => label);
}

function FormSection({
  label,
  required = false,
  mocked = false,
  action,
  children,
}: {
  label: string;
  required?: boolean;
  mocked?: boolean;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <h2 className={styles.sectionLabel}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </h2>
        {mocked && <span className={styles.mockedBadge}>Mocked</span>}
        {action && <div className={styles.sectionAction}>{action}</div>}
      </div>
      {children}
    </section>
  );
}

function isEquipmentRowComplete(item: Equipment): boolean {
  return item.role.trim() !== '' && item.type.trim() !== '' && item.asset.trim() !== '';
}

function isStepRowComplete(item: Step): boolean {
  return item.action.trim() !== '';
}

function isContingencyRowComplete(item: Contingency): boolean {
  return (
    item.id.trim() !== '' &&
    item.policyReference.trim() !== '' &&
    item.condition.trim() !== '' &&
    item.description.trim() !== ''
  );
}

/**
 * Guided-form view of a procedure. Fully editable local UI state — no
 * backend field or endpoint persists any of it yet (see `toProcedureDetail`'s
 * docstring), so closing/reopening this page discards whatever was changed,
 * same as the "Mocked" sections here.
 */
export default function ProcedureForm({
  procedure,
  original,
  classificationOptions,
  equipmentTypeOptions,
  onChange,
  hideChanges = false,
}: ProcedureFormProps) {
  const changes = diffLabels(original, procedure);
  const errors = validateProcedure(procedure);
  const canAddEquipment = procedure.affectedEquipment.every(isEquipmentRowComplete);
  const canAddStep = procedure.procedure.every(isStepRowComplete);
  const canAddContingency = procedure.contingencies.every(isContingencyRowComplete);

  // Required-field errors only show once a field has been visited and left
  // (blur) — a brand-new blank draft shouldn't open already covered in red.
  const [touched, setTouched] = useState<Partial<Record<RequiredFieldName, boolean>>>({});
  function markTouched(field: RequiredFieldName) {
    setTouched(prev => ({ ...prev, [field]: true }));
  }
  function fieldError(field: RequiredFieldName): string | undefined {
    return touched[field] ? errors[field] : undefined;
  }

  function update<K extends keyof ProcedureDetail>(key: K, value: ProcedureDetail[K]) {
    onChange({ ...procedure, [key]: value });
  }

  function updatePrerequisite(field: keyof ProcedureDetail['prerequisites'], value: string) {
    onChange({ ...procedure, prerequisites: { ...procedure.prerequisites, [field]: value } });
  }

  function updateEquipment(index: number, field: keyof Equipment, value: string) {
    const next = procedure.affectedEquipment.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    onChange({ ...procedure, affectedEquipment: next });
  }

  function addEquipment() {
    onChange({
      ...procedure,
      affectedEquipment: [...procedure.affectedEquipment, { role: '', type: '', asset: '' }],
    });
  }

  function removeEquipment(index: number) {
    onChange({
      ...procedure,
      affectedEquipment: procedure.affectedEquipment.filter((_, i) => i !== index),
    });
  }

  function updateStep(index: number, field: keyof Step, value: string | boolean) {
    const next = procedure.procedure.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    onChange({ ...procedure, procedure: next });
  }

  function addStep() {
    const nextNumber = procedure.procedure.reduce((max, s) => Math.max(max, s.step), 0) + 1;
    onChange({
      ...procedure,
      procedure: [
        ...procedure.procedure,
        {
          step: nextNumber,
          action: '',
          check: 'confirmation',
          passCriteria: '',
          gate: false,
          initials: '',
          source: '',
        },
      ],
    });
  }

  function removeStep(index: number) {
    onChange({ ...procedure, procedure: procedure.procedure.filter((_, i) => i !== index) });
  }

  function addContingency() {
    onChange({
      ...procedure,
      contingencies: [
        ...procedure.contingencies,
        { id: '', policyReference: '', condition: '', description: '' },
      ],
    });
  }

  function removeContingency(index: number) {
    onChange({
      ...procedure,
      contingencies: procedure.contingencies.filter((_, i) => i !== index),
    });
  }

  function updateContingency(index: number, field: keyof Contingency, value: string) {
    const next = procedure.contingencies.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    onChange({ ...procedure, contingencies: next });
  }

  function updateCloseout(field: keyof ProcedureDetail['closeout'], value: string) {
    onChange({ ...procedure, closeout: { ...procedure.closeout, [field]: value } });
  }

  function updateGovernance<K extends keyof ProcedureDetail['governance']>(
    field: K,
    value: ProcedureDetail['governance'][K]
  ) {
    onChange({ ...procedure, governance: { ...procedure.governance, [field]: value } });
  }

  return (
    <div className={styles.form}>
      <FormSection label="Metadata">
        <TextInput
          id="procedure-title"
          labelText={<RequiredLabel>Title</RequiredLabel>}
          value={procedure.title}
          onChange={e => update('title', e.target.value)}
          onBlur={() => markTouched('title')}
          invalid={Boolean(fieldError('title'))}
          invalidText={fieldError('title')}
        />
        <div className={styles.metadataRow}>
          <TextInput
            id="procedure-number"
            labelText={<RequiredLabel>Procedure #</RequiredLabel>}
            value={procedure.procedureNumber}
            onChange={e => update('procedureNumber', e.target.value)}
            onBlur={() => markTouched('procedureNumber')}
            invalid={Boolean(fieldError('procedureNumber'))}
            invalidText={fieldError('procedureNumber')}
          />
          <TextInput
            id="procedure-version"
            labelText="Version"
            value={`v${procedure.version}`}
            disabled
          />
          <Select
            id="procedure-classification"
            labelText={<RequiredLabel>Classification</RequiredLabel>}
            value={procedure.classification}
            onChange={e => update('classification', e.target.value)}
            onBlur={() => markTouched('classification')}
            invalid={Boolean(fieldError('classification'))}
            invalidText={fieldError('classification')}
          >
            {!classificationOptions.includes(procedure.classification) && (
              <SelectItem
                value={procedure.classification}
                text={procedure.classification || 'Select'}
              />
            )}
            {classificationOptions.map(option => (
              <SelectItem key={option} value={option} text={option} />
            ))}
          </Select>
          <Select
            id="procedure-equipment-type"
            labelText={<RequiredLabel>Equipment type</RequiredLabel>}
            value={procedure.equipmentType}
            onChange={e => update('equipmentType', e.target.value)}
            onBlur={() => markTouched('equipmentType')}
            invalid={Boolean(fieldError('equipmentType'))}
            invalidText={fieldError('equipmentType')}
          >
            {!equipmentTypeOptions.includes(procedure.equipmentType) && (
              <SelectItem
                value={procedure.equipmentType}
                text={procedure.equipmentType || 'Select'}
              />
            )}
            {equipmentTypeOptions.map(option => (
              <SelectItem key={option} value={option} text={option} />
            ))}
          </Select>
        </div>
        <TextArea
          id="procedure-description"
          labelText={<RequiredLabel>Description of work</RequiredLabel>}
          value={procedure.description}
          onChange={e => update('description', e.target.value)}
          onBlur={() => markTouched('description')}
          rows={3}
          invalid={Boolean(fieldError('description'))}
          invalidText={fieldError('description')}
        />
      </FormSection>

      <FormSection
        label="Affected equipment"
        action={
          <Button kind="tertiary" size="sm" onClick={addEquipment} disabled={!canAddEquipment}>
            + Add
          </Button>
        }
      >
        {procedure.affectedEquipment.length === 0 ? (
          <div className={styles.emptyNote}>No affected equipment listed.</div>
        ) : (
          <div className={styles.tableScroll}>
            <Table size="md" className={`${styles.equipmentTable} ${styles.dataTable}`}>
              <TableHead>
                <TableRow>
                  <TableHeader>Role</TableHeader>
                  <TableHeader>Type</TableHeader>
                  <TableHeader>Asset</TableHeader>
                  <TableHeader></TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {procedure.affectedEquipment.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <input
                        className={styles.cellInput}
                        aria-label={`Role ${index + 1}`}
                        value={item.role}
                        onChange={e => updateEquipment(index, 'role', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        className={styles.cellInput}
                        aria-label={`Type ${index + 1}`}
                        value={item.type}
                        onChange={e => updateEquipment(index, 'type', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        className={styles.cellInput}
                        aria-label={`Asset ${index + 1}`}
                        value={item.asset}
                        onChange={e => updateEquipment(index, 'asset', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        className={styles.removeRowBtn}
                        aria-label={`Remove equipment ${index + 1}`}
                        onClick={() => removeEquipment(index)}
                      >
                        <Close size={14} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </FormSection>

      <FormSection label="Prerequisites" mocked>
        <div className={styles.metadataRow2}>
          <TextInput
            id="procedure-qualifications"
            labelText="Qualifications"
            value={procedure.prerequisites.qualifications}
            onChange={e => updatePrerequisite('qualifications', e.target.value)}
          />
          <TextInput
            id="procedure-approvals"
            labelText="Approvals"
            value={procedure.prerequisites.approvals}
            onChange={e => updatePrerequisite('approvals', e.target.value)}
          />
        </div>
        <TextArea
          id="procedure-precondition"
          labelText="Precondition"
          value={procedure.prerequisites.precondition}
          onChange={e => updatePrerequisite('precondition', e.target.value)}
          rows={2}
        />
      </FormSection>

      <FormSection
        label="Procedure"
        required
        action={
          <Button kind="tertiary" size="sm" onClick={addStep} disabled={!canAddStep}>
            + Add step
          </Button>
        }
      >
        {procedure.procedure.length === 0 ? (
          <div className={styles.emptyNoteError}>{errors.procedure}</div>
        ) : (
          <div className={styles.tableScroll}>
            <Table size="md" className={`${styles.wideTable} ${styles.dataTable}`}>
              <TableHead>
                <TableRow>
                  <TableHeader>#</TableHeader>
                  <TableHeader>Step</TableHeader>
                  <TableHeader>Check</TableHeader>
                  <TableHeader>
                    Pass criteria
                    <span className={styles.mockedBadge}>Mocked</span>
                  </TableHeader>
                  <TableHeader>Gate</TableHeader>
                  <TableHeader>Initials</TableHeader>
                  <TableHeader>Source</TableHeader>
                  <TableHeader></TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {procedure.procedure.map((step, index) => (
                  <TableRow key={step.step}>
                    <TableCell>{step.step}</TableCell>
                    <TableCell>
                      <input
                        className={styles.cellInput}
                        aria-label={`Step ${step.step} action`}
                        value={step.action}
                        onChange={e => updateStep(index, 'action', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        id={`step-check-${index}`}
                        hideLabel
                        labelText={`Step ${step.step} check`}
                        size="sm"
                        className={styles.cellSelect}
                        value={step.check}
                        onChange={e => updateStep(index, 'check', e.target.value)}
                      >
                        <SelectItem value="confirmation" text="Confirmation" />
                        <SelectItem value="checkbox" text="Checkbox" />
                      </Select>
                    </TableCell>
                    <TableCell>
                      <input
                        className={styles.cellInput}
                        aria-label={`Step ${step.step} pass criteria`}
                        value={step.passCriteria}
                        onChange={e => updateStep(index, 'passCriteria', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        type="checkbox"
                        aria-label={`Step ${step.step} gate`}
                        checked={step.gate}
                        onChange={e => updateStep(index, 'gate', e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        className={styles.cellInput}
                        aria-label={`Step ${step.step} initials`}
                        value={step.initials}
                        onChange={e => updateStep(index, 'initials', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        className={styles.cellInput}
                        aria-label={`Step ${step.step} source`}
                        value={step.source}
                        onChange={e => updateStep(index, 'source', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        className={styles.removeRowBtn}
                        aria-label={`Remove step ${step.step}`}
                        onClick={() => removeStep(index)}
                      >
                        <Close size={14} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </FormSection>

      <FormSection
        label="Contingencies"
        mocked
        action={
          <Button kind="tertiary" size="sm" onClick={addContingency} disabled={!canAddContingency}>
            + Add
          </Button>
        }
      >
        {procedure.contingencies.length === 0 ? (
          <div className={styles.emptyNote}>No contingencies listed.</div>
        ) : (
          <div className={styles.contingencyList}>
            {procedure.contingencies.map((row, index) => (
              <div className={styles.contingencyCard} key={index}>
                <div className={styles.metadataRow2}>
                  <TextInput
                    id={`contingency-id-${index}`}
                    labelText="ID"
                    value={row.id}
                    onChange={e => updateContingency(index, 'id', e.target.value)}
                  />
                  <TextInput
                    id={`contingency-policy-${index}`}
                    labelText="Policy reference"
                    value={row.policyReference}
                    onChange={e => updateContingency(index, 'policyReference', e.target.value)}
                  />
                </div>
                <TextArea
                  id={`contingency-condition-${index}`}
                  labelText="Condition"
                  value={row.condition}
                  onChange={e => updateContingency(index, 'condition', e.target.value)}
                  rows={2}
                />
                <TextArea
                  id={`contingency-description-${index}`}
                  labelText="Description"
                  value={row.description}
                  onChange={e => updateContingency(index, 'description', e.target.value)}
                  rows={3}
                />
                <Button kind="danger--tertiary" size="sm" onClick={() => removeContingency(index)}>
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}
      </FormSection>

      <FormSection label="Closeout" mocked>
        <div className={styles.closeoutRow}>
          <TextInput
            id="procedure-turnover-from"
            labelText="Turnover from"
            value={procedure.closeout.turnoverFrom}
            onChange={e => updateCloseout('turnoverFrom', e.target.value)}
          />
          <TextInput
            id="procedure-turnover-to"
            labelText="Turnover to"
            value={procedure.closeout.turnoverTo}
            onChange={e => updateCloseout('turnoverTo', e.target.value)}
          />
          <TextInput
            id="procedure-signoff-role"
            labelText="Sign-off role"
            value={procedure.closeout.signoffRole}
            onChange={e => updateCloseout('signoffRole', e.target.value)}
          />
        </div>
      </FormSection>

      <FormSection label="Governance">
        <div className={styles.governanceCheckboxes}>
          <Checkbox
            id="procedure-mop-required"
            labelText="MOP required"
            checked={procedure.governance.mopRequired}
            onChange={(_, { checked }) => updateGovernance('mopRequired', checked)}
          />
          <Checkbox
            id="procedure-change-approval"
            labelText="Change approval"
            checked={procedure.governance.changeApproval}
            onChange={(_, { checked }) => updateGovernance('changeApproval', checked)}
          />
          <Checkbox
            id="procedure-loto"
            labelText="LOTO"
            checked={procedure.governance.loto}
            onChange={(_, { checked }) => updateGovernance('loto', checked)}
          />
        </div>
        <div className={styles.metadataRow2}>
          <TextInput
            id="procedure-redundancy"
            labelText="Redundancy"
            value={procedure.governance.redundancy}
            onChange={e => updateGovernance('redundancy', e.target.value)}
          />
          <TextInput
            id="procedure-escalates-to-eop"
            labelText="Escalates to EOP"
            value={procedure.governance.escalatesToEop}
            onChange={e => updateGovernance('escalatesToEop', e.target.value)}
          />
        </div>
      </FormSection>

      {!hideChanges && (
        <div className={styles.changesBlock}>
          <h2 className={styles.changesLabel}>Changes since v{original.version}</h2>
          {changes.length === 0 ? (
            <div className={styles.emptyNote}>No changes yet.</div>
          ) : (
            <ul className={styles.changesList}>
              {changes.map(label => (
                <li key={label}>{label}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
